import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface PersonnelProject {
  id: string;
  company_id: string;
  project_number: string | null;
  name: string;
  customer_id: string | null;
  location_city: string | null;
  location_state: string | null;
  client_po_number: string | null;
  nte_amount: number | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  customers?: { name: string } | null;
}

export interface RateSchedule {
  id: string;
  company_id: string;
  personnel_project_id: string;
  role_name: string;
  bill_rate_regular: number;
  bill_rate_ot: number;
  cost_rate: number;
  ot_threshold_hours: number;
  worker_count: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export interface TMWorker {
  id: string;
  company_id: string;
  personnel_project_id: string;
  rate_schedule_id: string;
  worker_name: string;
  vendor_id: string | null;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  tm_rate_schedules?: RateSchedule;
}

export interface TMTimesheet {
  id: string;
  company_id: string;
  personnel_project_id: string;
  week_ending: string;
  week_number: number | null;
  status: string;
  submitted_at: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  invoice_id: string | null;
  total_regular_hours: number;
  total_ot_hours: number;
  total_billed: number;
  total_cost: number;
  margin_pct: number;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface TMTimesheetLine {
  id: string;
  company_id: string;
  timesheet_id: string;
  personnel_project_id: string;
  worker_id: string;
  rate_schedule_id: string;
  mon_hours: number;
  tue_hours: number;
  wed_hours: number;
  thu_hours: number;
  fri_hours: number;
  sat_hours: number;
  sun_hours: number;
  total_hours: number;
  regular_hours: number;
  ot_hours: number;
  regular_billed: number;
  ot_billed: number;
  total_billed: number;
  regular_cost: number;
  ot_cost: number;
  total_cost: number;
  margin_amount: number;
  created_at: string;
  tm_workers?: TMWorker;
  tm_rate_schedules?: RateSchedule;
}

export function usePersonnelProjects() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["personnel_projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("personnel_projects" as any)
        .select("*, customers(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PersonnelProject[];
    },
    enabled: !!user,
  });
}

export function usePersonnelProject(id: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["personnel_project", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("personnel_projects" as any)
        .select("*, customers(name)")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as unknown as PersonnelProject;
    },
    enabled: !!user && !!id,
  });
}

export function useRateSchedules(projectId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["tm_rate_schedules", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tm_rate_schedules" as any)
        .select("*")
        .eq("personnel_project_id", projectId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as RateSchedule[];
    },
    enabled: !!user && !!projectId,
  });
}

export function useTMWorkers(projectId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["tm_workers", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tm_workers" as any)
        .select("*, tm_rate_schedules(*)")
        .eq("personnel_project_id", projectId!)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as unknown as TMWorker[];
    },
    enabled: !!user && !!projectId,
  });
}

export function useTMTimesheets(projectId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["tm_timesheets", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tm_timesheets" as any)
        .select("*")
        .eq("personnel_project_id", projectId!)
        .order("week_ending", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as TMTimesheet[];
    },
    enabled: !!user && !!projectId,
  });
}

export function useTMTimesheetLines(timesheetId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["tm_timesheet_lines", timesheetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tm_timesheet_lines" as any)
        .select("*, tm_workers(*), tm_rate_schedules(*)")
        .eq("timesheet_id", timesheetId!);
      if (error) throw error;
      return (data ?? []) as unknown as TMTimesheetLine[];
    },
    enabled: !!user && !!timesheetId,
  });
}

export function useCreatePersonnelProject() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      project: Partial<PersonnelProject>;
      rates: Array<{
        role_name: string;
        bill_rate_regular: number;
        cost_rate: number;
        ot_threshold_hours: number;
        worker_count: number;
        sort_order: number;
      }>;
      workers: Array<{
        rateIndex: number;
        worker_name: string;
        vendor_id: string | null;
      }>;
    }) => {
      // Get company_id
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("company_id")
        .eq("user_id", user!.id)
        .single();
      if (!profile) throw new Error("No company found");
      const companyId = profile.company_id;

      // Create project
      const { data: proj, error: projErr } = await supabase
        .from("personnel_projects" as any)
        .insert({
          ...input.project,
          company_id: companyId,
          created_by: user!.id,
        })
        .select()
        .single();
      if (projErr) throw projErr;
      const project = proj as any;

      // Create rate schedules
      const rateInserts = input.rates.map((r) => ({
        company_id: companyId,
        personnel_project_id: project.id,
        ...r,
      }));
      const { data: rates, error: rateErr } = await supabase
        .from("tm_rate_schedules" as any)
        .insert(rateInserts)
        .select();
      if (rateErr) throw rateErr;
      const rateRows = rates as any[];

      // Create workers
      if (input.workers.length > 0) {
        const workerInserts = input.workers.map((w) => ({
          company_id: companyId,
          personnel_project_id: project.id,
          rate_schedule_id: rateRows[w.rateIndex]?.id,
          worker_name: w.worker_name,
          vendor_id: w.vendor_id || null,
          start_date: input.project.start_date || null,
        }));
        const { error: workerErr } = await supabase
          .from("tm_workers" as any)
          .insert(workerInserts);
        if (workerErr) throw workerErr;
      }

      return project;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["personnel_projects"] });
      toast.success("Personnel project created");
    },
    onError: (e: any) => toast.error(e.message),
  });
}
